import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  Check,
  Wind,
  Compass,
  Zap,
  Heart,
  Sprout,
  BookOpen,
  Scale,
  Waves,
} from "lucide-react";
import Logo from "../components/Logo";
import TimeBackground from "../components/TimeBackground";
import { TRADITIONS, SEEKING_OPTIONS, ONBOARDING_BACKGROUNDS } from "../lib/constants";
import { useAuth } from "../lib/auth";

const ICONS = { Wind, Compass, Zap, Heart, Sprout, BookOpen, Scale, Waves };

export default function Onboarding() {
  const nav = useNavigate();
  const { user, updateProfile } = useAuth();
  const [step, setStep] = useState(0);
  const [tradition, setTradition] = useState(user?.tradition || null);
  const [seeking, setSeeking] = useState(user?.seeking || []);
  const [busy, setBusy] = useState(false);

  const toggleSeek = (id) =>
    setSeeking((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const canContinue = () => {
    if (step === 1) return !!tradition;
    if (step === 2) return seeking.length > 0;
    return true;
  };

  const next = async () => {
    if (step < 2) {
      setStep((s) => s + 1);
      return;
    }
    try {
      setBusy(true);
      await updateProfile({ tradition, seeking, onboarded: true });
      nav("/home", { replace: true });
    } finally {
      setBusy(false);
    }
  };

  const steps = [
    // 0 - Welcome
    (
      <motion.div
        key="welcome"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -12 }}
        className="flex flex-col items-center text-center gap-6 pt-6"
      >
        <Logo size={84} />
        <div className="space-y-2">
          <p className="text-[11px] tracking-[0.35em] uppercase text-[#d4af7a] font-ui">
            A welcome
          </p>
          <p className="font-ui font-normal text-[#f8ecd4] text-[20px] leading-[1.5] max-w-sm">
            A direct answer teaches you what to do.
            <br />
            A story teaches you who to become.
          </p>
        </div>
      </motion.div>
    ),
    // 1 - Tradition picker
    (
      <motion.div
        key="tradition"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -12 }}
        className="space-y-5"
      >
        <div className="text-center space-y-1.5">
          <h2 className="font-ui font-semibold text-[24px] text-gold-gradient">Whose voice moves you?</h2>
          <p className="text-[#ac9470] text-sm font-ui">
            Choose a storyteller. You can change this anytime.
          </p>
        </div>
        <div className="space-y-3">
          {TRADITIONS.map((t) => {
            const active = tradition === t.id;
            return (
              <button
                key={t.id}
                data-testid={`tradition-${t.id}`}
                onClick={() => setTradition(t.id)}
                className={`w-full text-left glass rounded-2xl p-4 flex items-center gap-4 transition-all border ${
                  active
                    ? "border-[#eac88a]/70 shadow-[0_0_0_1px_rgba(234,200,138,0.5),0_0_30px_rgba(234,200,138,0.22)]"
                    : "border-[#d4af7a]/15 hover:border-[#d4af7a]/40"
                }`}
              >
                <div
                  className="w-14 h-14 rounded-xl bg-cover bg-center flex-shrink-0 ring-1 ring-[#d4af7a]/25"
                  style={{ backgroundImage: `url(${t.image})` }}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-ui font-semibold text-[#f8ecd4] text-[17px] leading-tight">
                      {t.name}
                    </h3>
                    {active && (
                      <div className="w-5 h-5 rounded-full bg-gradient-to-br from-[#eac88a] to-[#a07544] flex items-center justify-center">
                        <Check size={12} strokeWidth={3} className="text-[#1a0d1d]" />
                      </div>
                    )}
                  </div>
                  <p className="text-[10.5px] tracking-[0.2em] uppercase text-[#d4af7a] font-ui mt-0.5">
                    {t.subtitle}
                  </p>
                  <p className="text-[#f8ecd4]/70 text-[13px] mt-1.5 font-ui leading-snug">
                    {t.description}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      </motion.div>
    ),
    // 2 - Seeking (final step)
    (
      <motion.div
        key="seeking"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -12 }}
        className="space-y-5"
      >
        <div className="text-center space-y-1.5">
          <h2 className="font-ui font-semibold text-[24px] text-gold-gradient">What are you seeking?</h2>
          <p className="text-[#ac9470] text-sm font-ui">Choose one or many.</p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {SEEKING_OPTIONS.map((opt) => {
            const Icon = ICONS[opt.icon];
            const active = seeking.includes(opt.id);
            return (
              <button
                key={opt.id}
                data-testid={`seeking-${opt.id}`}
                onClick={() => toggleSeek(opt.id)}
                className={`glass rounded-2xl py-4 px-3 flex flex-col items-center gap-2 text-center transition-all border ${
                  active
                    ? "border-[#eac88a]/70 bg-gradient-to-br from-[#eac88a]/10 to-[#a07544]/10"
                    : "border-[#d4af7a]/15 hover:border-[#d4af7a]/40"
                }`}
              >
                <Icon
                  size={22}
                  strokeWidth={1.5}
                  className={active ? "text-[#eac88a]" : "text-[#ac9470]"}
                />
                <span className="font-ui text-xs text-[#f8ecd4]/90 leading-tight">
                  {opt.label}
                </span>
              </button>
            );
          })}
        </div>
      </motion.div>
    ),
  ];

  return (
    <TimeBackground overlay={0.72} image={ONBOARDING_BACKGROUNDS[step]}>
      <div className="min-h-screen max-w-md mx-auto w-full px-6 py-8 flex flex-col">
        <div className="flex gap-1.5 mb-6">
          {steps.map((_, i) => (
            <div
              key={i}
              className={`flex-1 h-[3px] rounded-full transition-all duration-500 ${
                i <= step
                  ? "bg-gradient-to-r from-[#eac88a] to-[#a07544]"
                  : "bg-white/10"
              }`}
            />
          ))}
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto py-2">
          <AnimatePresence mode="wait">{steps[step]}</AnimatePresence>
        </div>

        <div className="pt-4 space-y-2">
          <button
            data-testid="onboarding-continue"
            onClick={next}
            disabled={!canContinue() || busy}
            className="btn-gold w-full rounded-full py-3.5 flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <span className="font-ui">
              {busy
                ? "One moment…"
                : step === 2
                ? "Enter Narrava"
                : "Continue"}
            </span>
            {!busy && <ArrowRight size={17} strokeWidth={2} />}
          </button>
          {step > 0 && (
            <button
              data-testid="onboarding-back"
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              className="btn-ghost-gold w-full py-2 text-xs tracking-[0.18em] uppercase font-ui"
            >
              ← Back
            </button>
          )}
        </div>
      </div>
    </TimeBackground>
  );
}